import { DataGrid, GridColumnProps, SelectFilterComponent, SelectFloatingFilterComponent, SelectSearchFloatingFilterComponent } from '@roc/ui';
import { observer } from 'mobx-react';
import { Box, Grid, makeStyles } from '@material-ui/core';
import { useEffect } from 'react';
import { useBalanceSheetReportStore } from '../../hooks/useBalanceSheetReportStore';
import { DateFormat, formatDate } from '@roc/feature-utils';
import { dateFilterProps, filterProps, formatCurrency, noOpComparator } from '@roc/ui/utils';
import { BalanceSheetReportToolbar } from './balanceSheetReportToolbar';
import { BalanceSheetReportTabs } from '../../utils/constants';

const useStyles = makeStyles(theme => ({
  gridContainer: {
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
    height: '65vh',
    overflow: 'hidden',
  },
}));

export const rentalColumns: GridColumnProps[] = [
  {
    field: 'loanId',
    headerName: 'Loan Id',
    minWidth: 90,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'productType',
    headerName: 'Product Type',
    minWidth: 125,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'tpo',
    headerName: 'TPO',
    minWidth: 125,
    comparator: noOpComparator,
    ...filterProps(),
  },
    {
    field: 'closingDate',
    headerName: 'Closing Date',
    minWidth: 80,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'fundingDate',
    headerName: 'Funding Date',
    minWidth: 80,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'wireOutDate',
    headerName: 'Wire Out Date',
    minWidth: 80,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'acquisition',
    headerName: 'Acquisition',
    minWidth: 125,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'fico',
    headerName: 'FICO',
    minWidth: 80,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'loanAmount',
    headerName: 'Loan Amount',
    minWidth: 120,
    cellRenderer: 'currencyCellRenderer',
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'holdingEntity',
    headerName: 'Holding Entity',
    minWidth: 120,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'fundingEntity',
    headerName: 'Funding Entity',
    minWidth: 120,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'pendingTrade',
    headerName: 'Pending Trade',
    minWidth: 90,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'pendingAppraisals',
    headerName: 'Pending Appraisals',
    minWidth: 80,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'rateLockCounterparty',
    headerName: 'Rate Lock Counterparty',
    minWidth: 80,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'loanDocsReceived',
    headerName: 'Loan Docs Received',
    minWidth: 80,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'loanDocsImageReceived',
    headerName: 'Loan Docs Image Received',
    minWidth: 80,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'propLoan',
    headerName: 'Prop Loan',
    minWidth: 80,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'attorney',
    headerName: 'Attorney',
    minWidth: 80,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'detailedMbaPayString',
    headerName: 'Ever 90+ DQ',
    minWidth: 80,
    comparator: noOpComparator,
    ...filterProps(),
  },
];

export const bridgeColumns: GridColumnProps[] = [
  {
    field: 'loanId',
    headerName: 'Loan Id',
    minWidth: 90,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'loanSubType',
    headerName: 'Loan Sub Type',
    minWidth: 125,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'tpo',
    headerName: 'TPO',
    minWidth: 125,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'numberOfProperties',
    headerName: 'Number of Properties',
    minWidth: 90,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'closingDate',
    headerName: 'Closing Date',
    cellRenderer: 'dateCellRenderer',
    minWidth: 125,
    comparator: noOpComparator,
    ...dateFilterProps(),
  },
  {
    field: 'fundingDate',
    headerName: 'Funding Date',
    minWidth: 80,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'wireOutDate',
    headerName: 'Wire Out Date',
    minWidth: 80,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'interestRate',
    headerName: 'Interest Rate',
    minWidth: 90,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'fico',
    headerName: 'FICO',
    minWidth: 80,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'totalLoanAmount',
    headerName: 'Total Loan Amount',
    minWidth: 120,
    cellRenderer: 'currencyCellRenderer',
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'initialLoanAmount',
    headerName: 'Initial Loan Amount',
    minWidth: 120,
    cellRenderer: 'currencyCellRenderer',
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'upbDrawnAmount',
    headerName: 'UPB Drawn Amount',
    minWidth: 120,
    cellRenderer: 'currencyCellRenderer',
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'initialConstructionHoldback',
    headerName: 'Initial Construction Holdback',
    minWidth: 120,
    cellRenderer: 'currencyCellRenderer',
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'acquisitionPrice',
    headerName: 'Acquisition Price',
    minWidth: 120,
    cellRenderer: 'currencyCellRenderer',
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'completedRenovations',
    headerName: 'Completed Renovations',
    minWidth: 120,
    cellRenderer: 'currencyCellRenderer',
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'currentCost',
    headerName: 'Current Cost',
    minWidth: 120,
    cellRenderer: 'currencyCellRenderer',
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'renovationBudget',
    headerName: 'Renovation Budget',
    minWidth: 120,
    cellRenderer: 'currencyCellRenderer',
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'asIsValue',
    headerName: 'As Is Value',
    minWidth: 120,
    cellRenderer: 'currencyCellRenderer',
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'afterRepairValue',
    headerName: 'After Repair Value',
    minWidth: 120,
    cellRenderer: 'currencyCellRenderer',
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'appraisalDate',
    headerName: 'Appraisal Date',
    cellRenderer: 'dateCellRenderer',
    minWidth: 125,
    comparator: noOpComparator,
    ...dateFilterProps(),
  },
  {
    field: 'purchaseDate',
    headerName: 'Purchase Date',
    cellRenderer: 'dateCellRenderer',
    minWidth: 125,
    comparator: noOpComparator,
    ...dateFilterProps(),
  },
  {
    field: 'settlementCosts',
    headerName: 'Settlement Costs',
    minWidth: 120,
    cellRenderer: 'currencyCellRenderer',
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'ltcc',
    headerName: 'LTCC',
    minWidth: 80,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'box',
    headerName: 'Box',
    minWidth: 80,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'holdingEntity',
    headerName: 'Holding Entity',
    minWidth: 120,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'fundingEntity',
    headerName: 'Funding Entity',
    minWidth: 120,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'pendingTrade',
    headerName: 'Pending Trade',
    minWidth: 90,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'pendingAppraisals',
    headerName: 'Pending Appraisals',
    minWidth: 80,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'loanDocsReceived',
    headerName: 'Loan Docs Received',
    minWidth: 80,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'loanDocsImageReceived',
    headerName: 'Loan Docs Image Received',
    minWidth: 80,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'propLoan',
    headerName: 'Prop Loan',
    minWidth: 80,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'attorney',
    headerName: 'Attorney',
    minWidth: 80,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'detailedMbaPayString',
    headerName: 'Ever 90+ DQ',
    minWidth: 80,
    comparator: noOpComparator,
    ...filterProps(),
  },
];

const columnsByTab: Record<BalanceSheetReportTabs, GridColumnProps[]> = {
  [BalanceSheetReportTabs.RENTAL_LOANS]: rentalColumns,
  [BalanceSheetReportTabs.BRIDGE_LOANS]: bridgeColumns,
  [BalanceSheetReportTabs.GROUND_UP_LOANS]: bridgeColumns,
};

export const BalanceSheetReportGrid = observer(() => {
  const classes = useStyles();
  const { balanceSheetReportStore } = useBalanceSheetReportStore();
  const { balanceSheetReportGridStore: servicingTransfersGridStore, tab } = balanceSheetReportStore;

  useEffect(() => {
    balanceSheetReportStore.fetchDashboard();
    servicingTransfersGridStore.resetAndFetchGridData();
  }, []);

  useEffect(() => {
    onTabChange();
  }, [tab]);

  const onTabChange = () => {
    servicingTransfersGridStore.resetAndFetchGridData();
  };

  const frameworkComponents = {
    selectFloatingFilter: SelectFloatingFilterComponent,
    selectSearchFloatingFilter: SelectSearchFloatingFilterComponent,
    selectFilter: SelectFilterComponent,
    dateCellRenderer: ({ value }) =>
      formatDate(value, DateFormat.MMDDYYYY) ?? null,
    currencyCellRenderer({ value }) {
      return value ? formatCurrency(value) : '-';
    },
  };

  const onSortChanged = params => {
    const sortModel = params.api.getSortModel();
    servicingTransfersGridStore.setSortModel(sortModel);
  };

  const onFilterChanged = params => {
    const filterModel = params.api.getFilterModel();
    servicingTransfersGridStore.setFilterModel(filterModel);
  };

  return (
    <Box>
      <Grid item className={classes.gridContainer}>
        <DataGrid
          columns={columnsByTab[tab]}
          rows={servicingTransfersGridStore.gridData.data.rows}
          frameworkComponents={frameworkComponents}
          headerCheckboxSelection={true}
          suppressRowClickSelection={true}
          key={tab}
          toolbar={<BalanceSheetReportToolbar />}
          onSortChanged={onSortChanged}
          onFilterChanged={onFilterChanged}
          isLoading={servicingTransfersGridStore.isLoading}
          showFilters={servicingTransfersGridStore.showFilters}
          sortModel={servicingTransfersGridStore.sortModel}
          filterModel={servicingTransfersGridStore.filterModel}
          paginationData={servicingTransfersGridStore.gridData.meta}
          setPageNumber={servicingTransfersGridStore.setPageNumber}
          getRowNodeId={(data) => data.loanId}
        />
      </Grid>
    </Box>
  );
});
